import React, { useState, useCallback, useEffect, useRef } from 'react';
import { useToast } from '../Toast/ToastContext';
import { getFailedOrders, removeFailedOrder, retryOrder, getMaxAffordableQuantity } from '../../services/orderService';
import { getLotSizes, getFreezeLimits } from '../../services/generalDataCache';
import QuantityInput from '../common/QuantityInput';
import eventBus, { EVENTS } from '../../services/eventBus';
import './FailedOrders.css';

const FailedOrdersTable = React.memo(() => {
  const { showSuccess, showError } = useToast();

  // Internal state - isolated from parent
  const [failedOrders, setFailedOrders] = useState([]);
  const [failedOrderQuantities, setFailedOrderQuantities] = useState({});
  const [loadingStates, setLoadingStates] = useState({});
  const [maxQuantities, setMaxQuantities] = useState({});
  const [removingOrders, setRemovingOrders] = useState(new Set());
  const [lotSizes, setLotSizes] = useState({});
  const [freezeLimits, setFreezeLimits] = useState({});

  // Ref to store interval ID (prevents double intervals in React Strict Mode)
  const intervalRef = useRef(null);

  // Fetch ALL failed orders (no filters)
  const fetchFailedOrders = async () => {
    try {
      const orders = await getFailedOrders();
      setFailedOrders(orders || []);
    } catch (error) {
      console.error('[FailedOrders] Failed to fetch failed orders:', error);
      // Don't show error toast - failed orders are optional feature
    }
  };

  // Fetch lot sizes on mount
  useEffect(() => {
    const fetchLotSizes = async () => {
      try {
        const lotSizesData = await getLotSizes();
        const freezeLimitsData = await getFreezeLimits();

        if (lotSizesData) setLotSizes(lotSizesData);
        if (freezeLimitsData) setFreezeLimits(freezeLimitsData);
      } catch (error) {
        console.error("Failed to fetch lot sizes:", error);
      }
    };

    fetchLotSizes();
  }, []);

  // Fetch on mount only (NO auto-refresh to prevent blinking)
  useEffect(() => {
    fetchFailedOrders();

    // Listen for failed order events from order placement
    const unsubscribe = eventBus.on(EVENTS.FAILED_ORDER_CREATED, () => {
      // Wait 1 second for backend to save failed order to Redis
      setTimeout(() => {
        fetchFailedOrders();
      }, 1000);
    });

    // Cleanup function
    return () => {
      unsubscribe(); // Remove event listener
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  // Format timestamp to readable format (IST)
  const formatTime = (timestamp) => {
    const date = new Date(timestamp);
    const hours = date.getHours().toString().padStart(2, '0');
    const minutes = date.getMinutes().toString().padStart(2, '0');
    return `${hours}:${minutes}`;
  };

  // Get time elapsed since failure
  const getTimeElapsed = (timestamp) => {
    const now = new Date();
    const diff = Math.floor((now - new Date(timestamp)) / 1000);

    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    return `${Math.floor(diff / 3600)}h ago`;
  };

  // Handle retry with current quantity
  const handleRetry = useCallback(async (order) => {
    // Prevent double-clicks
    if (loadingStates[order.id]) return;

    // Mark as removing (optimistic UI)
    setRemovingOrders(prev => new Set(prev).add(order.id));
    setLoadingStates(prev => ({ ...prev, [order.id]: true }));

    try {
      const quantity = failedOrderQuantities[order.id] || order.quantity;
      await retryOrder(order.id, quantity, order.index, order.expiry);
      showSuccess('Order retried successfully!');

      // Clear interval to prevent race condition
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Fetch updated list
      await fetchFailedOrders();

      // Restart interval with fresh 30s countdown
      intervalRef.current = setInterval(() => {
        fetchFailedOrders();
      }, 30000);
    } catch (error) {
      // On error, restore the row
      setRemovingOrders(prev => {
        const next = new Set(prev);
        next.delete(order.id);
        return next;
      });
      setLoadingStates(prev => ({ ...prev, [order.id]: false }));
      showError(`Retry failed: ${error.message}`);
    }
  }, [failedOrderQuantities, loadingStates, showSuccess, showError]);

  // Handle remove
  const handleRemove = useCallback(async (orderId) => {
    // Prevent double-clicks
    if (loadingStates[orderId]) return;

    // Mark as removing (optimistic UI)
    setRemovingOrders(prev => new Set(prev).add(orderId));
    setLoadingStates(prev => ({ ...prev, [orderId]: true }));

    try {
      // Find the order to get its index and expiry
      const order = failedOrders.find(o => o.id === orderId);
      if (!order) {
        showError('Order not found');
        setRemovingOrders(prev => {
          const next = new Set(prev);
          next.delete(orderId);
          return next;
        });
        setLoadingStates(prev => ({ ...prev, [orderId]: false }));
        return;
      }

      await removeFailedOrder(orderId, order.index, order.expiry);
      showSuccess('Failed order removed');

      // Clear interval to prevent race condition
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }

      // Fetch updated list
      await fetchFailedOrders();

      // Restart interval with fresh 30s countdown
      intervalRef.current = setInterval(() => {
        fetchFailedOrders();
      }, 30000);
    } catch (error) {
      // On error, restore the row
      setRemovingOrders(prev => {
        const next = new Set(prev);
        next.delete(orderId);
        return next;
      });
      setLoadingStates(prev => ({ ...prev, [orderId]: false }));
      showError(`Remove failed: ${error.message}`);
    }
  }, [failedOrders, loadingStates, showSuccess, showError]);

  // Get max affordable quantity for an order
  const handleGetMaxAffordable = useCallback(async (order) => {
    const key = order.id;
    if (maxQuantities[key]) return; // Already fetched

    try {
      const data = await getMaxAffordableQuantity(
        order.order_params.tradingsymbol,
        order.price
      );
      setMaxQuantities(prev => ({ ...prev, [key]: data.max_quantity }));
    } catch (error) {
      console.error('Failed to get max affordable quantity:', error);
    }
  }, [maxQuantities]);

  // Handle quantity change
  const handleQuantityChange = useCallback((orderId, newQuantity) => {
    // Clear max quantity when manually changed
    setMaxQuantities(prev => {
      const updated = { ...prev };
      delete updated[orderId];
      return updated;
    });
    setFailedOrderQuantities(prev => ({
      ...prev,
      [orderId]: parseInt(newQuantity)
    }));
  }, []);

  // Helper functions for lot sizes
  const getLotSizeForOrder = (order) => {
    return lotSizes[order.index];
  };

  const getFreezeLimitForOrder = (order) => {
    return freezeLimits[order.index];
  };

  const generateQuantityOptions = (lotSize, freezeLimit) => {
    if (!lotSize || !freezeLimit) return [];

    const options = new Set();
    const maxLots = Math.floor(freezeLimit / lotSize);

    // Step 1: Add first 5 multiples for granular control
    for (let i = 1; i <= Math.min(5, maxLots); i++) {
      options.add(i * lotSize);
    }

    // Step 2: Add every 5th multiple for larger quantities
    for (let i = 10; i <= maxLots; i += 5) {
      options.add(i * lotSize);
    }

    // Step 3: Always ensure freeze_limit is included
    options.add(freezeLimit);

    return Array.from(options).sort((a, b) => a - b);
  };

  // Always render component to keep event listener active
  // Hide with CSS when no failed orders
  const hasFailedOrders = failedOrders && failedOrders.length > 0;

  return (
    <div
      className="failed-orders-container"
      style={{ display: hasFailedOrders ? 'block' : 'none' }}
    >
      <div className="failed-orders-header">
        <h3>Failed Orders</h3>
        <span className="failed-count">{failedOrders.length}</span>
      </div>

      <div className="failed-orders-table-wrapper">
        <table className="failed-orders-table">
          <thead>
            <tr>
              <th>TIME</th>
              <th>INDEX</th>
              <th>STRIKE</th>
              <th>TYPE</th>
              <th>QTY</th>
              <th>REASON</th>
              <th>PRICE</th>
              <th>ACTION</th>
            </tr>
          </thead>
          <tbody>
            {failedOrders.map((order) => {
              const isLoading = loadingStates[order.id];
              const maxQty = maxQuantities[order.id];
              const hasReachedRetryLimit = order.retry_count >= 3;
              const isRemoving = removingOrders.has(order.id);
              const currentQuantity = failedOrderQuantities[order.id] || order.quantity;

              return (
                <tr key={order.id} className={`failed-order-row ${isRemoving ? 'removing' : ''}`}>
                  {/* Time */}
                  <td className="time-cell" data-label="Time">
                    <div className="time-info">
                      <span className="time">{formatTime(order.timestamp)}</span>
                      <span className="time-elapsed">{getTimeElapsed(order.timestamp)}</span>
                    </div>
                  </td>

                  {/* Index */}
                  <td className="index-cell" data-label="Index">
                    <span className="index-badge">{order.index || 'N/A'}</span>
                  </td>

                  {/* Strike */}
                  <td className="strike-cell" data-label="Strike">
                    {order.strike}
                  </td>

                  {/* Option Type */}
                  <td className="type-cell" data-label="Type">
                    <span className={`type-badge ${order.option_type.toLowerCase()}`}>
                      {order.option_type}
                    </span>
                  </td>

                  {/* Quantity with Dropdown */}
                  <td className="quantity-cell" data-label="Qty">
                    <div className="quantity-wrapper">
                      <QuantityInput
                        value={currentQuantity}
                        onChange={(newQty) => handleQuantityChange(order.id, newQty)}
                        lotSize={getLotSizeForOrder(order)}
                        freezeLimit={getFreezeLimitForOrder(order)}
                        suggestions={generateQuantityOptions(
                          getLotSizeForOrder(order),
                          getFreezeLimitForOrder(order)
                        )}
                        containerClassName="extra-compact"
                        disabled={isLoading || hasReachedRetryLimit}
                      />
                      {maxQty && (
                        <span className="max-suggestion">Max: {maxQty}</span>
                      )}
                    </div>
                  </td>

                  {/* Reason */}
                  <td className="reason-cell" data-label="Reason">
                    <div className="reason-wrapper">
                      <span className="reason-text">{order.reason}</span>
                      {order.error_code && (
                        <span className="error-code">{order.error_code}</span>
                      )}
                      {order.retry_count > 0 && (
                        <span className="retry-badge">{order.retry_count} retries</span>
                      )}
                    </div>
                  </td>

                  {/* Price */}
                  <td className="price-cell" data-label="Price">
                    ₹{order.price.toFixed(2)}
                  </td>

                  {/* Actions */}
                  <td className="actions-cell" data-label="Action">
                    <div className="action-buttons">
                      {!hasReachedRetryLimit ? (
                        <>
                          <button
                            className="retry-btn"
                            onClick={() => handleRetry(order)}
                            disabled={isLoading}
                            title="Retry"
                          >
                            {isLoading ? '⟳' : '↻'}
                          </button>
                          <button
                            className="remove-btn"
                            onClick={() => handleRemove(order.id)}
                            disabled={isLoading}
                            title="Remove"
                          >
                            ×
                          </button>
                          {!maxQty && (
                            <button
                              className="max-btn"
                              onClick={() => handleGetMaxAffordable(order)}
                              disabled={isLoading}
                              title="Max affordable"
                            >
                              Max
                            </button>
                          )}
                        </>
                      ) : (
                        <div className="retry-limit-reached">
                          <span className="limit-text">Limit</span>
                          <button
                            className="remove-btn"
                            onClick={() => handleRemove(order.id)}
                            disabled={isLoading}
                            title="Remove"
                          >
                            ×
                          </button>
                        </div>
                      )}
                    </div>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="failed-orders-footer">
        <div className="footer-info">
          <span className="info-label">Auto-clear: 6hr • Max retries: 3</span>
        </div>
      </div>
    </div>
  );
});

FailedOrdersTable.displayName = 'FailedOrdersTable';

export default FailedOrdersTable;
